/**
 * Screen 2: pick a time. One engine answers (OB-22); picking a time takes a
 * 5-minute hold (OB-03) and a taken time offers the next three (OB-04).
 * Emergency types show today and tomorrow soonest first, with "Call now"
 * and "Request a call" when nothing is open (OB-10). A family member gets
 * back-to-back suggestions first (OB-20).
 */
import type {
  Attribution,
  Availability,
  Hold,
  PublicSite,
  PublicType,
  Slot,
} from '@velora/contracts/booking';
import { Avatar, Button, Skeleton } from '@velora/ui';
import {
  AlertTriangle,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Clock,
  Phone,
  UsersRound,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FamilyAnchor } from '../flow.js';
import { alternativesOf, messageOf, type BookingApi } from '../lib/api.js';
import { radioKeyDown, radioTabIndex } from '../lib/radio.js';
import {
  addDays,
  dayLabel,
  dayParts,
  lengthLabel,
  localDate,
  localMinutes,
  partOfDay,
  telHref,
  timeLabel,
  whenLabel,
} from '../lib/format.js';
import { useAsync, useRefresh } from '../lib/hooks.js';
import { Callback } from './Callback.js';

const WINDOW_DAYS = 7;

interface Props {
  api: BookingApi;
  site: PublicSite;
  type: PublicType;
  providerId: string | null;
  hold: Hold | null;
  notice: string | null;
  family: FamilyAnchor | null;
  visitorId: string;
  attribution: Attribution;
  phoneLabel: string | null;
  onProvider: (id: string | null) => void;
  onHeld: (hold: Hold) => void;
  onChangeType: () => void;
}

/** The slots shown for a day: one engine list ("anyone") or one provider's. */
export function slotsFor(av: Availability, providerId: string | null, date: string): Slot[] {
  if (providerId === null) return av.anyone.find((d) => d.date === date)?.slots ?? [];
  return (
    av.providers.find((p) => p.providerId === providerId)?.days.find((d) => d.date === date)
      ?.slots ?? []
  );
}

export function TimeStep(p: Props) {
  const { site, type, api } = p;
  const tz = site.timezone;
  const [from, setFrom] = useState(site.today);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alts, setAlts] = useState<Slot[]>([]);

  const near = p.family
    ? { start: p.family.start, end: p.family.end, providerId: p.family.providerId }
    : undefined;
  const key = `${type.id}|${from}|${p.family?.reference ?? ''}`;
  const av = useAsync(key, () =>
    api.availability({
      typeId: type.id,
      from,
      days: type.urgent ? 2 : WINDOW_DAYS,
      ...(near ? { near } : {}),
    }),
  );
  // Live: every 30 s and when the tab comes back (OB-02).
  useRefresh(!busy, 30_000, av.reload);

  const data = av.data;
  const dates = data?.dates ?? [];
  const counts = new Map<string, number>();
  if (data) for (const d of dates) counts.set(d, slotsFor(data, p.providerId, d).length);
  const firstWithSlots = dates.find((d) => (counts.get(d) ?? 0) > 0) ?? null;
  // A picked day that has since filled up (live refresh) falls back to the
  // first day that still has times.
  const day =
    picked && (counts.get(picked) ?? 0) > 0 ? picked : (firstWithSlots ?? dates[0] ?? null);
  const slots = data && day ? slotsFor(data, p.providerId, day) : [];
  const providerById = new Map(site.providers.map((x) => [x.id, x]));
  const offered = data
    ? site.providers.filter((x) => data.providers.some((ap) => ap.providerId === x.id))
    : [];
  // Index of the checked radio in [First available, ...offered].
  const providerIndex =
    p.providerId === null ? 0 : offered.findIndex((x) => x.id === p.providerId) + 1;
  const emptyWindow = data !== null && dates.every((d) => (counts.get(d) ?? 0) === 0);
  const providerNext =
    data && p.providerId
      ? (data.providers.find((x) => x.providerId === p.providerId)?.nextAvailable ?? null)
      : null;
  const nextOpening = p.providerId ? providerNext : (data?.firstAvailable ?? null);

  const take = async (slot: Slot, providerId: string | null) => {
    setBusy(slot.start + (providerId ?? ''));
    setError(null);
    setAlts([]);
    try {
      const hold = await api.hold({
        typeId: type.id,
        providerId,
        start: slot.start,
        visitorId: p.visitorId,
        ...(p.hold ? { replace: { holdId: p.hold.holdId, holdToken: p.hold.holdToken } } : {}),
      });
      p.onHeld(hold);
    } catch (err) {
      setError(messageOf(err));
      setAlts(alternativesOf(err));
      av.reload();
    } finally {
      setBusy(null);
    }
  };

  const byPart = new Map<string, Slot[]>();
  for (const s of slots) {
    const part = partOfDay(localMinutes(s.start, tz));
    byPart.set(part, [...(byPart.get(part) ?? []), s]);
  }
  const groups = [...byPart.entries()];

  // Keep the chosen day in view in the scrolling strip (phones).
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [day, data]);

  const canEarlier = from > site.today && !type.urgent;
  const canLater = data ? addDays(from, WINDOW_DAYS) <= data.latest && !type.urgent : false;

  return (
    <div className="bw-step" data-testid="step-time">
      <div className="bw-picked">
        <span className="bw-picked__label">{type.label}</span>
        <span className="bw-dim">
          {type.urgent ? 'Soonest first' : lengthLabel(type.lengthMin)}
        </span>
        <Button variant="ghost" size="sm" onClick={p.onChangeType}>
          Change
        </Button>
      </div>

      {p.notice ? (
        <div className="bw-banner" data-tone="warning" role="status">
          <Clock size={18} aria-hidden />
          <p>{p.notice}</p>
        </div>
      ) : null}

      {type.urgent ? (
        <div className="bw-banner" data-tone="danger" data-testid="urgent-banner">
          <AlertTriangle size={18} aria-hidden />
          <p>
            Showing today and tomorrow, soonest first.
            {site.phone ? ' If you are in severe pain or swelling is spreading, call us now.' : ''}
          </p>
          {site.phone ? (
            <Button asChild size="sm" variant="primary">
              <a href={telHref(site.phone)}>
                <Phone size={14} aria-hidden /> Call
              </a>
            </Button>
          ) : null}
        </div>
      ) : null}

      {p.family && data && data.suggestions.length > 0 ? (
        <section className="bw-section" aria-labelledby="bw-family">
          <h2 id="bw-family" className="bw-q">
            <UsersRound size={18} aria-hidden /> Back to back with {p.family.patientFirstName}
          </h2>
          <div className="bw-suggest" data-testid="family-suggestions">
            {data.suggestions.slice(0, 4).map((s) => {
              const prov = providerById.get(s.providerId);
              return (
                <button
                  key={s.start + s.providerId}
                  type="button"
                  className="bw-suggest__item"
                  disabled={busy !== null}
                  onClick={() => void take(s, s.providerId)}
                >
                  <span className="bw-suggest__time">{timeLabel(s.start, tz)}</span>
                  <span className="bw-suggest__meta">
                    {dayLabel(localDate(s.start, tz), site.today)} ·{' '}
                    {prov?.displayName ?? 'Our team'}
                  </span>
                  <span className="bw-suggest__why">{s.reason}</span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {offered.length > 1 ? (
        <section className="bw-section" aria-label="Provider">
          <div className="bw-providers" role="radiogroup" aria-label="Who you see">
            <button
              type="button"
              role="radio"
              aria-checked={p.providerId === null}
              tabIndex={radioTabIndex(0, providerIndex)}
              className="bw-prov"
              onClick={() => p.onProvider(null)}
              onKeyDown={radioKeyDown}
            >
              <span className="bw-prov__any" aria-hidden>
                <CalendarClock size={16} />
              </span>
              <span className="bw-prov__name">First available</span>
            </button>
            {offered.map((x, i) => (
              <button
                key={x.id}
                type="button"
                role="radio"
                aria-checked={p.providerId === x.id}
                tabIndex={radioTabIndex(i + 1, providerIndex)}
                className="bw-prov"
                onClick={() => p.onProvider(x.id)}
                onKeyDown={radioKeyDown}
              >
                <Avatar name={x.displayName.replace(/^Dr\. /, '')} size={28} />
                <span className="bw-prov__name">{x.displayName}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="bw-section" aria-labelledby="bw-days">
        <div className="bw-days__head">
          <h2 id="bw-days" className="bw-q">
            {type.urgent ? 'Soonest times' : 'Choose a day'}
          </h2>
          {!type.urgent ? (
            <div className="bw-days__nav">
              <button
                type="button"
                className="bw-iconbtn"
                aria-label="Earlier days"
                disabled={!canEarlier}
                onClick={() => {
                  setFrom((f) => {
                    const prev = addDays(f, -WINDOW_DAYS);
                    return prev < site.today ? site.today : prev;
                  });
                  setPicked(null);
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                className="bw-iconbtn"
                aria-label="Later days"
                disabled={!canLater}
                onClick={() => {
                  setFrom((f) => addDays(f, WINDOW_DAYS));
                  setPicked(null);
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          ) : null}
        </div>

        {av.loading && !data ? (
          <div className="bw-days" aria-busy="true">
            {Array.from({ length: type.urgent ? 2 : WINDOW_DAYS }, (_, i) => (
              <Skeleton key={i} width={64} height={76} radius="lg" />
            ))}
          </div>
        ) : (
          <div className="bw-days" role="tablist" aria-label="Days" ref={strip}>
            {dates.map((d) => {
              const parts = dayParts(d);
              const n = counts.get(d) ?? 0;
              return (
                <button
                  key={d}
                  type="button"
                  role="tab"
                  aria-selected={d === day}
                  className="bw-day"
                  data-empty={n === 0 ? '' : undefined}
                  disabled={n === 0}
                  onClick={() => setPicked(d)}
                  aria-label={`${dayLabel(d, site.today)}, ${n === 0 ? 'no times' : `${n} times`}`}
                >
                  <span className="bw-day__dow">
                    {d === site.today ? 'Today' : d === addDays(site.today, 1) ? 'Tmrw' : parts.dow}
                  </span>
                  <span className="bw-day__num">{parts.day}</span>
                  <span className="bw-day__mon">{parts.month}</span>
                  <span className="bw-day__dot" aria-hidden />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {av.error && !data ? (
        <div className="bw-banner" data-tone="danger" role="alert">
          <AlertTriangle size={18} aria-hidden />
          <p>{messageOf(av.error)}</p>
          <Button size="sm" variant="secondary" onClick={av.reload}>
            Try again
          </Button>
        </div>
      ) : null}

      {error ? (
        <div className="bw-banner" data-tone="warning" role="alert" data-testid="hold-error">
          <AlertTriangle size={18} aria-hidden />
          <div>
            <p>{error}</p>
            {alts.length > 0 ? (
              <div className="bw-alts">
                {alts.map((s) => (
                  <Button
                    key={s.start + s.providerId}
                    size="sm"
                    variant="secondary"
                    onClick={() => void take(s, p.providerId === null ? null : s.providerId)}
                  >
                    {whenLabel(s.start, tz, site.today)}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {data && day && slots.length > 0 ? (
        <section
          className="bw-section"
          aria-label={`Times on ${dayLabel(day, site.today)}`}
          data-testid="times"
        >
          <p className="bw-dayhead">
            {dayLabel(day, site.today)}
            <span className="bw-dim"> · times in {tzName(tz)}</span>
          </p>
          {groups.map(([part, list]) => (
            <div key={part} className="bw-times__group">
              <h3 className="bw-times__part">{part}</h3>
              <div className="bw-times">
                {list.map((s) => {
                  const id = s.start + (p.providerId ?? '');
                  const held = p.hold?.start === s.start;
                  const prov = providerById.get(s.providerId);
                  return (
                    <button
                      key={s.start + s.providerId}
                      type="button"
                      className="bw-time"
                      data-held={held ? '' : undefined}
                      aria-busy={busy === id}
                      disabled={busy !== null}
                      onClick={() => void take(s, p.providerId)}
                      title={prov && p.providerId === null ? `with ${prov.displayName}` : undefined}
                    >
                      {timeLabel(s.start, tz)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {emptyWindow ? (
        <section className="bw-section bw-none" data-testid="no-times">
          {nextOpening && !type.urgent ? (
            <div className="bw-next">
              <p>
                Nothing open{' '}
                {p.providerId
                  ? `with ${providerById.get(p.providerId)?.displayName ?? 'this provider'} `
                  : ''}
                these days. The next opening is{' '}
                <strong>{whenLabel(nextOpening.start, tz, site.today)}</strong>.
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  const d = localDate(nextOpening.start, tz);
                  setFrom(d);
                  setPicked(d);
                }}
              >
                Show that day
              </Button>
            </div>
          ) : null}
          {type.urgent || !nextOpening ? (
            <Callback
              api={api}
              visitorId={p.visitorId}
              practiceName={site.practiceName}
              officePhone={site.phone}
              officePhoneLabel={p.phoneLabel}
              kind={type.urgent ? 'emergency' : 'callback'}
              typeId={type.id}
              attribution={p.attribution}
              bot={site.turnstile}
              title={
                type.urgent ? 'No emergency times are open right now' : 'No times are open online'
              }
              lead={
                type.urgent
                  ? 'Call us now, or leave your number and we will call you back within 15 minutes during office hours.'
                  : 'Leave your number and the office will call you to find a time.'
              }
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function tzName(tz: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((x) => x.type === 'timeZoneName');
    return part?.value ?? tz;
  } catch {
    return tz;
  }
}
